import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { api } from "@/lib/api";

const ResQShield = ({ size = 40 }) => (
  <svg width={size} height={size * 1.18} viewBox="0 0 60 71" xmlns="http://www.w3.org/2000/svg" aria-label="ResQ1">
    <defs>
      <clipPath id={`lg-clip-${size}`}>
        <path d="M4 6 Q4 1 9 1 L51 1 Q56 1 56 6 L56 38 Q56 55 30 70 Q4 55 4 38 Z" />
      </clipPath>
    </defs>
    <g clipPath={`url(#lg-clip-${size})`}>
      <rect width="60" height="71" fill="#E01A2E" />
      <path d="M-4 44 L30 20 L37 28 L3 56 Z" fill="#F7F5F0" />
    </g>
  </svg>
);

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const forgot = async () => {
    setError("");
    if (!email) return setError("Enter your email address first, then tap Forgot password.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setNotice(data.message || "If that email is registered, a reset link is on its way.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(email, password);
      if (user.role === "admin") nav("/admin");
      else if (user.role === "client") nav("/portal");
      else nav("/admin"); // shows the member-account notice instead of looping
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{ background: "#F7F5F0", fontFamily: "'Inter', system-ui, sans-serif" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10 no-underline" data-testid="login-home-link">
          <ResQShield size={30} />
          <div className="leading-tight">
            <div className="font-black text-xl tracking-tighter text-[#1A1E24]">
              RESQ<span style={{ color: "#E01A2E" }}>1</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#5B6B7E] font-mono-data mt-0.5">
              Emergency Protection
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-3 text-[10px] font-mono-data uppercase tracking-[0.18em] text-[#5B6B7E] mb-4">
          <span className="w-6 h-px bg-[#E01A2E]" />
          RESQCONNECT OPS
        </div>
        <h1
          className="text-4xl leading-[1.05] tracking-tight text-[#1A1E24] mb-3"
          style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500, letterSpacing: "-0.022em" }}
        >
          Sign in to your{" "}
          <em style={{ fontStyle: "italic", color: "#5B6B7E", fontWeight: 400 }}>dispatch console</em>.
        </h1>
        <p className="text-[#5B6B7E] text-sm mb-8">
          Operators and portal users. Contact ResQ1 dispatch if you need access.
        </p>

        <form onSubmit={submit} className="grid gap-5">
          <div>
            <label className="text-[10px] font-mono-data font-semibold uppercase tracking-[0.18em] text-[#5B6B7E]">
              Email or username
            </label>
            <input
              className="tactical-input mt-2"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email-input"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[10px] font-mono-data font-semibold uppercase tracking-[0.18em] text-[#5B6B7E]">
              Password
            </label>
            <input
              className="tactical-input mt-2"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
            />
          </div>
          {error && (
            <div
              className="text-[#E01A2E] text-xs px-3 py-2 rounded"
              style={{ background: "rgba(224,26,46,0.06)", border: "1px solid rgba(224,26,46,0.2)" }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}
          {notice && (
            <div
              className="text-[#0F5A32] text-xs px-3 py-2 rounded"
              style={{ background: "rgba(30,158,99,0.08)", border: "1px solid rgba(30,158,99,0.3)" }}
              data-testid="login-notice"
            >
              {notice}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="mt-2 text-white font-medium text-sm py-3 rounded-full transition-opacity disabled:opacity-40"
            style={{ background: "#1A1E24", letterSpacing: "-0.005em" }}
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : "Sign in →"}
          </button>
          <button
            type="button"
            onClick={forgot}
            disabled={busy}
            className="text-[11px] font-mono-data uppercase tracking-[0.16em] text-[#5B6B7E] hover:text-[#1A1E24] transition-colors"
            data-testid="login-forgot-button"
          >
            Forgot password?
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-[#1A1E24]/8 flex justify-between text-[10px] font-mono-data uppercase tracking-[0.16em] text-[#a8b3c1]">
          <Link to="/" className="hover:text-[#5B6B7E] transition-colors">← Back to resq1.co.za</Link>
          <span>Rescue One (Pty) Ltd</span>
        </div>
      </div>
    </div>
  );
}
